/**
 * Utility functions for handling and formatting API errors
 */

interface ValidationError {
  type: string;
  loc: (string | number)[];
  msg: string;
  input?: any;
  ctx?: Record<string, any>;
}

interface ApiErrorResponse {
  detail?: string | ValidationError[];
  message?: string;
}

/**
 * Structured field error from backend
 */
export interface FieldError {
  field: string;
  message: string;
  message_en?: string;
  value?: string;
  conflicting_record?: string;
}

/**
 * Structured error response from backend (409, 422 with field_errors)
 */
export interface StructuredErrorResponse {
  detail: string;
  field_errors?: FieldError[];
  error_code?: string;
}

/**
 * Format a single validation error into a human-readable Thai message
 */
function formatValidationError(error: ValidationError): string {
  const field = error.loc.slice(1).join(' → '); // Skip 'body' prefix
  const fieldThai = translateFieldName(field);
  
  // Format the error message based on type
  switch (error.type) {
    case 'string_too_long':
      return `${fieldThai}: ข้อความยาวเกินไป (สูงสุด ${error.ctx?.max_length} ตัวอักษร)`;
    
    case 'string_too_short':
      return `${fieldThai}: ข้อความสั้นเกินไป (ต้องมีอย่างน้อย ${error.ctx?.min_length} ตัวอักษร)`;
    
    case 'string_pattern_mismatch':
      return `${fieldThai}: รูปแบบไม่ถูกต้อง`;
    
    case 'missing':
      return `${fieldThai}: จำเป็นต้องกรอก`;
    
    case 'value_error':
      return `${fieldThai}: ค่าไม่ถูกต้อง`;
    
    case 'type_error':
      return `${fieldThai}: ประเภทข้อมูลไม่ถูกต้อง`;
    
    default:
      return `${fieldThai}: ${error.msg}`;
  }
}

/**
 * Translate field names to Thai
 */
function translateFieldName(field: string): string {
  const translations: Record<string, string> = {
    // Company fields
    'name': 'ชื่อบริษัท',
    'company_code': 'รหัสบริษัท',
    'tax_id': 'เลขประจำตัวผู้เสียภาษี',
    'house_number': 'บ้านเลขที่',
    'moo': 'หมู่',
    'building': 'อาคาร',
    'soi': 'ซอย',
    'road': 'ถนน',
    'subdistrict': 'ตำบล/แขวง',
    'district': 'อำเภอ/เขต',
    'province': 'จังหวัด',
    'postal_code': 'รหัสไปรษณีย์',
    'notes': 'หมายเหตุ',
    
    // Shipping address fields
    'shipping_address → recipient_name': 'ชื่อผู้รับเอกสาร',
    'shipping_address → recipient_phone': 'เบอร์โทรผู้รับเอกสาร',
    'shipping_address → house_number': 'บ้านเลขที่ (ที่อยู่จัดส่ง)',
    'shipping_address → moo': 'หมู่ (ที่อยู่จัดส่ง)',
    'shipping_address → building': 'อาคาร (ที่อยู่จัดส่ง)',
    'shipping_address → soi': 'ซอย (ที่อยู่จัดส่ง)',
    'shipping_address → road': 'ถนน (ที่อยู่จัดส่ง)',
    'shipping_address → subdistrict': 'ตำบล/แขวง (ที่อยู่จัดส่ง)',
    'shipping_address → district': 'อำเภอ/เขต (ที่อยู่จัดส่ง)',
    'shipping_address → province': 'จังหวัด (ที่อยู่จัดส่ง)',
    'shipping_address → postal_code': 'รหัสไปรษณีย์ (ที่อยู่จัดส่ง)',
    
    // Customer fields
    'customer_type': 'ประเภทลูกค้า',
    'first_name': 'ชื่อ',
    'last_name': 'นามสกุล',
    'company_name': 'ชื่อบริษัท',
    'id_card': 'เลขบัตรประชาชน',
    'phone': 'เบอร์โทรศัพท์',
    'email': 'อีเมล',
    'date_of_birth': 'วันเกิด',
    
    // Agent fields
    'agent_code': 'รหัสตัวแทน',
    'license_number': 'เลขที่ใบอนุญาต',
    'license_type': 'ประเภทใบอนุญาต',
    'license_expiry_date': 'วันหมดอายุใบอนุญาต',
    
    // Product fields
    'product_name': 'ชื่อผลิตภัณฑ์',
    'product_code': 'รหัสผลิตภัณฑ์',
    'insurance_type': 'ประเภทประกัน',
    'company_id': 'บริษัทประกัน',
    
    // Policy fields
    'policy_number': 'เลขที่กรมธรรม์',
    'start_date': 'วันที่เริ่มคุ้มครอง',
    'end_date': 'วันที่สิ้นสุดคุ้มครอง',
    'premium': 'เบี้ยประกัน',
    'sum_insured': 'ทุนประกัน',
    
    // Team fields
    'team_name': 'ชื่อทีม',
    'team_code': 'รหัสทีม',
    'leader_id': 'หัวหน้าทีม',
  };
  
  return translations[field] || field;
}

/**
 * Extract and format error message from API error response
 */
export function formatApiError(error: any): string {
  // Check if it's an axios error with response
  if (!error.response?.data) {
    return error.message || 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ';
  }
  
  const data: ApiErrorResponse = error.response.data;
  
  // Handle validation errors (array format)
  if (Array.isArray(data.detail)) {
    const errors = data.detail.map(formatValidationError);
    
    if (errors.length === 1) {
      return errors[0];
    }
    
    return `พบข้อผิดพลาด ${errors.length} รายการ:\n${errors.map((e, i) => `${i + 1}. ${e}`).join('\n')}`;
  }
  
  // Handle string detail message
  if (typeof data.detail === 'string') {
    return data.detail;
  }
  
  // Handle message field
  if (data.message) {
    return data.message;
  }
  
  // Fallback
  return 'เกิดข้อผิดพลาดในการบันทึกข้อมูล';
}

/**
 * Extract validation errors as a structured object for form field highlighting
 */
export function extractFieldErrors(error: any): Record<string, string> {
  const fieldErrors: Record<string, string> = {};
  
  if (!error.response?.data?.detail || !Array.isArray(error.response.data.detail)) {
    return fieldErrors;
  }
  
  const validationErrors: ValidationError[] = error.response.data.detail;
  
  validationErrors.forEach((err) => {
    const field = err.loc.slice(1).join('.'); // Skip 'body' prefix
    fieldErrors[field] = formatValidationError(err);
  });
  
  return fieldErrors;
}

/**
 * Check if error is a validation error (422)
 */
export function isValidationError(error: any): boolean {
  return error.response?.status === 422;
}

/**
 * Check if error is an authentication error (401)
 */
export function isAuthError(error: any): boolean {
  return error.response?.status === 401;
}

/**
 * Check if error is a not found error (404)
 */
export function isNotFoundError(error: any): boolean {
  return error.response?.status === 404;
}

/**
 * Get a user-friendly error message based on status code
 */
export function getErrorMessage(error: any, defaultMessage: string = 'เกิดข้อผิดพลาด'): string {
  if (!error.response) {
    return 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
  }
  
  const status = error.response.status;
  
  switch (status) {
    case 400:
      return formatApiError(error);
    case 401:
      return 'กรุณาเข้าสู่ระบบใหม่';
    case 403:
      return 'คุณไม่มีสิทธิ์ในการดำเนินการนี้';
    case 404:
      return 'ไม่พบข้อมูลที่ต้องการ';
    case 422:
      return formatApiError(error);
    case 500:
      return 'เกิดข้อผิดพลาดภายในเซิร์ฟเวอร์';
    default:
      return formatApiError(error) || defaultMessage;
  }
}

/**
 * Parse API error response and extract field-specific errors
 * Handles both 409 Conflict errors and 422 Validation errors with field_errors
 * 
 * @param error - Axios error object
 * @returns Array of field errors with field name, message, and optional metadata
 */
export function parseFieldErrors(error: any): FieldError[] {
  if (!error.response?.data) {
    return [];
  }

  const data = error.response.data;
  const status = error.response.status;

  // Handle structured error responses (409 Conflict with field_errors)
  if (data.field_errors && Array.isArray(data.field_errors)) {
    return data.field_errors.map((fieldError: FieldError) => ({
      field: fieldError.field,
      message: fieldError.message,
      message_en: fieldError.message_en,
      value: fieldError.value,
      conflicting_record: fieldError.conflicting_record,
    }));
  }

  // Handle 422 validation errors (Pydantic format)
  if (status === 422 && Array.isArray(data.detail)) {
    const validationErrors: ValidationError[] = data.detail;
    return validationErrors.map((err) => {
      const field = err.loc.slice(1).join('.'); // Skip 'body' prefix
      return {
        field,
        message: formatValidationError(err),
      };
    });
  }

  // Try to parse duplicate error from detail message (fallback for old format)
  if (status === 409 && typeof data.detail === 'string') {
    const duplicateInfo = parseDuplicateError(data.detail);
    if (duplicateInfo) {
      return [
        {
          field: duplicateInfo.field,
          message: getDuplicateErrorMessage(duplicateInfo.field),
          value: duplicateInfo.value,
          conflicting_record: duplicateInfo.existingRecord,
        },
      ];
    }
  }

  return [];
}

/**
 * Extract duplicate field information from 409 error message
 * Parses messages like: "เลขประจำตัวผู้เสียภาษี '1234567890123' มีอยู่ในระบบแล้ว (บริษัท: ABC Insurance)"
 * 
 * @param errorMessage - Error message string from API
 * @returns Object with field, value, and existing record name, or null if not a duplicate error
 */
export function parseDuplicateError(errorMessage: string): {
  field: string;
  value: string;
  existingRecord: string;
} | null {
  // Pattern for tax_id: เลขประจำตัวผู้เสียภาษี 'VALUE' มีอยู่ในระบบแล้ว (บริษัท: NAME)
  const taxIdMatch = errorMessage.match(/เลขประจำตัวผู้เสียภาษี\s*['"]([^'"]+)['"]\s*มีอยู่ในระบบแล้ว\s*\(บริษัท:\s*(.+)\)$/);
  if (taxIdMatch) {
    return {
      field: 'tax_id',
      value: taxIdMatch[1],
      existingRecord: taxIdMatch[2],
    };
  }

  // Pattern for registration_number: เลขทะเบียนบริษัท 'VALUE' มีอยู่ในระบบแล้ว (บริษัท: NAME)
  const regNumMatch = errorMessage.match(/เลขทะเบียนบริษัท\s*['"]([^'"]+)['"]\s*มีอยู่ในระบบแล้ว\s*\(บริษัท:\s*(.+)\)$/);
  if (regNumMatch) {
    return {
      field: 'registration_number',
      value: regNumMatch[1],
      existingRecord: regNumMatch[2],
    };
  }

  // Pattern for company_code: รหัสบริษัท 'VALUE' มีอยู่ในระบบแล้ว (บริษัท: NAME)
  const companyCodeMatch = errorMessage.match(/รหัสบริษัท\s*['"]([^'"]+)['"]\s*มีอยู่ในระบบแล้ว\s*\(บริษัท:\s*(.+)\)$/);
  if (companyCodeMatch) {
    return {
      field: 'company_code',
      value: companyCodeMatch[1],
      existingRecord: companyCodeMatch[2],
    };
  }

  // Pattern for id_card_number: เลขบัตรประชาชน 'VALUE' มีอยู่ในระบบแล้ว (ลูกค้า: NAME)
  const idCardMatch = errorMessage.match(/เลขบัตรประชาชน\s*['"]([^'"]+)['"]\s*มีอยู่ในระบบแล้ว\s*\(ลูกค้า:\s*(.+)\)$/);
  if (idCardMatch) {
    return {
      field: 'id_card_number',
      value: idCardMatch[1],
      existingRecord: idCardMatch[2],
    };
  }

  // Pattern for license_number: เลขที่ใบอนุญาต 'VALUE' มีอยู่ในระบบแล้ว (ตัวแทน: NAME)
  const licenseMatch = errorMessage.match(/เลขที่ใบอนุญาต\s*['"]([^'"]+)['"]\s*มีอยู่ในระบบแล้ว\s*\(ตัวแทน:\s*(.+)\)$/);
  if (licenseMatch) {
    return {
      field: 'license_number',
      value: licenseMatch[1],
      existingRecord: licenseMatch[2],
    };
  }

  // Pattern for product_code: รหัสผลิตภัณฑ์ 'VALUE' มีอยู่ในระบบแล้ว (ผลิตภัณฑ์: NAME)
  const productCodeMatch = errorMessage.match(/รหัสผลิตภัณฑ์\s*['"]([^'"]+)['"]\s*มีอยู่ในระบบแล้ว\s*\(ผลิตภัณฑ์:\s*(.+)\)$/);
  if (productCodeMatch) {
    return {
      field: 'product_code',
      value: productCodeMatch[1],
      existingRecord: productCodeMatch[2],
    };
  }

  return null;
}

/**
 * Map backend field names to frontend form field names
 * Ensures consistent field naming between API and form components
 * 
 * @param backendField - Field name from backend error response
 * @returns Mapped field name for frontend form
 */
export function mapBackendFieldToFormField(backendField: string): string {
  const fieldMap: Record<string, string> = {
    // Company fields
    'tax_id': 'tax_id',
    'registration_number': 'registration_number',
    'company_code': 'company_code',
    'name': 'name',
    'house_number': 'house_number',
    'moo': 'moo',
    'building': 'building',
    'soi': 'soi',
    'road': 'road',
    'subdistrict': 'subdistrict',
    'district': 'district',
    'province': 'province',
    'postal_code': 'postal_code',
    
    // Customer fields
    'id_card_number': 'id_card_number',
    'first_name': 'first_name',
    'last_name': 'last_name',
    'company_name': 'company_name',
    'phone': 'phone',
    'email': 'email',
    'date_of_birth': 'date_of_birth',
    
    // Agent fields
    'agent_code': 'agent_code',
    'license_number': 'license_number',
    'license_type': 'license_type',
    'license_expiry_date': 'license_expiry_date',
    
    // Product fields
    'product_code': 'product_code',
    'product_name': 'product_name',
    'insurance_type': 'insurance_type',
    'company_id': 'company_id',
    
    // Policy fields
    'policy_number': 'policy_number',
    'start_date': 'start_date',
    'end_date': 'end_date',
    'premium': 'premium',
    'sum_insured': 'sum_insured',
  };
  
  return fieldMap[backendField] || backendField;
}

/**
 * Get Thai error message for duplicate field
 * 
 * @param field - Field name that has duplicate value
 * @returns Thai error message
 */
function getDuplicateErrorMessage(field: string): string {
  const messages: Record<string, string> = {
    'tax_id': 'เลขประจำตัวผู้เสียภาษีนี้มีอยู่ในระบบแล้ว',
    'registration_number': 'เลขทะเบียนบริษัทนี้มีอยู่ในระบบแล้ว',
    'company_code': 'รหัสบริษัทนี้มีอยู่ในระบบแล้ว',
    'id_card_number': 'เลขบัตรประชาชนนี้มีอยู่ในระบบแล้ว',
    'license_number': 'เลขที่ใบอนุญาตนี้มีอยู่ในระบบแล้ว',
    'product_code': 'รหัสผลิตภัณฑ์นี้มีอยู่ในระบบแล้ว',
    'email': 'อีเมลนี้มีอยู่ในระบบแล้ว',
    'phone': 'เบอร์โทรศัพท์นี้มีอยู่ในระบบแล้ว',
  };
  
  return messages[field] || 'ข้อมูลนี้มีอยู่ในระบบแล้ว';
}

